import * as R from 'ramda';
import React, { useMemo } from 'react';
// components
import { Table } from '../../../components/table';
import { TextComponent } from '../../../components/text';
import { FormGroupTitleMultiple } from '../../../components/form-group-title-multiple';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hooks
import useEditViewHook from '../../../hooks/use-edit-view';
// ui
import { Box, Flex } from '../../../ui';
// feature rate
import { PulseButton } from '../ui';
import { availableRateFilters } from '../available-rates-filters';
import {
  CUSTOMER_AVAILABLE_RATES_REPORT_GUID,
  availableRatesAndCarriersTableSettings,
} from '../settings';
//////////////////////////////////////////////////

export const availableCarrierReport = {
  fields: [
    {
      width: 150,
      name: GC.FIELD_TOTAL,
    },
    {
      width: 200,
      name: GC.FIELD_CARRIER_CONTRACT_NAME,
    },
    {
      width: 200,
      name: GC.FIELD_CUSTOMER_CONTRACT_NAME,
    },
    {
      width: 150,
      name: `${GC.FIELD_PRICE}.${GC.FIELD_NAME}`,
    },
    {
      width: 150,
      name: GC.FIELD_TRANSPORTING_CARRIER,
    },
    {
      width: 200,
      name: GC.FIELD_NAME,
    },
    {
      width: 150,
      name: GC.FIELD_MODE,
    },
    {
      width: 250,
      name: GC.FIELD_CARRIER_SERVICE_TYPES,
    },
    {
      width: 250,
      name: GC.FIELD_CARRIER_EQUIPMENTS,
    },
    {
      width: 250,
      name: GC.FIELD_TRANSIT_DAYS,
    },
    {
      width: 150,
      name: GC.FIELD_CARRIER_MC_NUMBER,
    },
    {
      width: 150,
      name: GC.FIELD_CARRIER_USDOT_NUMBER,
    },
    {
      width: 150,
      name: GC.FIELD_CARRIER_SCAC,
    },
  ],
};

const CustomTotalComponent = ({ entity }) => {
  const total = R.pathOr('', ['data', GC.SYSTEM_OBJECT_NORMALIZED_TOTAL, GC.FIELD_TOTAL], entity);
  const currency = R.pathOr('', ['data', GC.SYSTEM_OBJECT_NORMALIZED_TOTAL, GC.FIELD_CURRENCY], entity);

  if (G.isNotNilAndNotEmpty(total)) {
    return (
      <Box
        p='4px 5px'
        fontWeight='bold'
        borderRadius='3px'
        width='max-content'
        bg={G.getTheme('common.linkColor')}
        color={G.getTheme('common.whiteTextColor')}
      >
        {G.getCurrencySymbol(currency)}{G.mathRoundNumber(total)} {currency}
      </Box>
    );
  }

  return null;
};

const TableCell = ({ text }) => {
  if (G.isNilOrEmpty(text)) return null;

  return (
    <TextComponent title={text} display='block' maxWidth='100%' withEllipsis={true}>
      {text}
    </TextComponent>
  );
};

export const availableRatesColumnSettings = {
  [GC.FIELD_NAME]: {
    width: 200,
    name: 'titles:carrier-name',
  },
  [GC.FIELD_MODE]: {
    width: 150,
    name: 'titles:mode',
    filter: R.prop(GC.FIELD_MODE, availableRateFilters),
    customComponent: ({ data }) =>
      <TableCell text={R.path([GC.FIELD_PRICE, GC.FIELD_MODE, GC.FIELD_DISPLAYED_VALUE], data)} />,
  },
  [GC.FIELD_TOTAL]: {
    width: 150,
    name: 'titles:total',
    styles: { titleInputMinWidth: 140 },
    filter: R.prop(GC.FIELD_TOTAL, availableRateFilters),
    customComponent: (entity) => (
      <CustomTotalComponent entity={entity} />
    ),
  },
  [GC.FIELD_CARRIER_SCAC]: {
    width: 150,
    name: 'titles:scac',
  },
  [GC.FIELD_TRANSIT_DAYS]: {
    width: 150,
    name: 'titles:transit-days',
    filter: R.prop(GC.FIELD_TRANSIT_DAYS, availableRateFilters),
    customComponent: ({ data }) =>
      <TableCell text={R.pathOr('', [GC.FIELD_PRICE, GC.FIELD_TRANSIT_DAYS], data)} />,
  },
  [GC.FIELD_CARRIER_MC_NUMBER]: {
    width: 150,
    name: 'titles:mc-number',
  },
  [GC.FIELD_CARRIER_EQUIPMENTS]: {
    width: 250,
    name: 'titles:equipment',
    filter: R.prop(GC.FIELD_CARRIER_EQUIPMENTS, availableRateFilters),
    customComponent: ({ data }) => {
      const equipments = R.path([GC.FIELD_PRICE, GC.FIELD_CARRIER_EQUIPMENTS], data);

      if (G.isNilOrEmpty(equipments)) return '';

      const text = R.compose(
        R.join(', '),
        R.map(R.prop(GC.FIELD_DISPLAYED_VALUE)),
      )(equipments);

      return <TableCell text={text} />;
    },
  },
  [GC.FIELD_CARRIER_USDOT_NUMBER]: {
    width: 150,
    name: 'titles:usdot-number',
  },
  [GC.FIELD_TRANSPORTING_CARRIER]: {
    width: 150,
    name: 'titles:transporting-carrier',
    filter: R.prop(GC.FIELD_TRANSPORTING_CARRIER, availableRateFilters),
    customComponent: ({ data }) =>
      <TableCell text={R.path([GC.FIELD_TRANSPORTING_CARRIER, GC.FIELD_NAME], data)} />,
  },
  [GC.FIELD_CARRIER_CONTRACT_NAME]: {
    width: 200,
    name: 'titles:contract-name',
  },
  [`${GC.FIELD_PRICE}.${GC.FIELD_NAME}`]: {
    width: 150,
    name: 'titles:rate-name',
    filter: R.prop(`${GC.FIELD_PRICE}.${GC.FIELD_NAME}`, availableRateFilters),
    customComponent: ({ data }) =>
      <TableCell text={R.path([GC.FIELD_PRICE, GC.FIELD_NAME], data)} />,
  },
  [GC.FIELD_CARRIER_SERVICE_TYPES]: {
    width: 250,
    name: 'titles:service-type',
    filter: R.prop(GC.FIELD_CARRIER_SERVICE_TYPES, availableRateFilters),
    customComponent: ({ data }) => {
      const serviceTypes = R.path([GC.FIELD_PRICE, GC.FIELD_CARRIER_SERVICE_TYPES], data);

      if (G.isNilOrEmpty(serviceTypes)) return '';

      const text = R.compose(
        R.join(', '),
        R.map(R.prop(GC.FIELD_DISPLAYED_VALUE)),
      )(serviceTypes);

      return <TableCell text={text} />;
    },
  },
  [GC.FIELD_CUSTOMER_CONTRACT_NAME]: {
    width: 200,
    name: 'titles:customer-contract-name',
  },
};

const AvailableRatesTable = (props) => {
  const {
    loading,
    rateInfo,
    onSelectRate,
    availableRates,
  } = props;

  const report = useMemo(() => {
    const fields = R.reject(
      ({ name }) => R.and(
        R.equals(name, GC.FIELD_TRANSPORTING_CARRIER),
        R.not(G.showTransportingCarrier()),
      ),
      availableCarrierReport.fields,
    );

    return {
      fields,
      [GC.FIELD_GUID]: CUSTOMER_AVAILABLE_RATES_REPORT_GUID,
      type: G.getWindowLocale('titles:available-rates', 'Available Rates'),
    };
  }, []);

  const defaultReportFields = useMemo(
    () => R.map(R.prop(GC.FIELD_NAME), report.fields),
    [report],
  );

  const { uiReportFields, handleOpenEditView } = useEditViewHook({
    report,
    defaultReportFields,
    columnSettings: availableRatesColumnSettings,
  });

  const sortedReport = useMemo(() => (
    G.isNilOrEmpty(uiReportFields) ? report : G.getSortedReport(uiReportFields, report)
  ), [report, uiReportFields]);

  return (
    <Table
      loading={loading}
      allChecked={false}
      showEditView={true}
      hasSelectable={true}
      report={sortedReport}
      shouldUseUIFilters={true}
      withResizableColumns={true}
      useSearchableColumns={true}
      useNewTitleFilterInputs={true}
      totalCount={availableRates.length}
      pagination={{ limit: 10, offset: 0 }}
      handleClickEditOrder={handleOpenEditView}
      columnSettings={availableRatesColumnSettings}
      tableSettings={availableRatesAndCarriersTableSettings}
      itemList={R.map(R.assoc('selected', false), availableRates)}
      onOptionClick={(guid, item) => onSelectRate(item, rateInfo)}
      filterProps={G.getFilterPropsFromColumnSettings(availableRatesColumnSettings)}
    />
  );
};

const AvailableRates = (props) => (
  <Box>
    <FormGroupTitleMultiple
      mb='0'
      jc='space-between'
      showArrowToggle={false}
      title={G.getWindowLocale('titles:available-carriers', 'Available Carriers')}
    >
      <Flex>
        {
          G.isNotNilAndNotEmpty(R.path(['rateInfo', 'ratingIntegrationErrors'], props)) &&
          <Box
            mx='10px'
            p='3px 15px'
            fontSize='11px'
            cursor='pointer'
            fontWeight='bold'
            borderRadius='2px'
            bg={G.getTheme('colors.white')}
            color={G.getTheme('colors.light.darkRed')}
            onClick={props.handleShowIntegrationErrors}
          >
            <PulseButton animationDuration={props.animationDuration}>
              {G.getWindowLocale('actions:show-integration-errors', 'Show Integration Errors')}
            </PulseButton>
          </Box>
        }
        <Box
          mx='10px'
          p='3px 10px'
          fontSize='11px'
          cursor='pointer'
          fontWeight='bold'
          borderRadius='2px'
          onClick={props.closeModal}
          bg={G.getTheme('colors.white')}
          color={G.getTheme('colors.light.darkRed')}
        >
          {G.getWindowLocale('actions:close', 'Close')}
        </Box>
      </Flex>
    </FormGroupTitleMultiple>
    <AvailableRatesTable {...props} />
  </Box>
);

export default AvailableRates;
